import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';

import { clientPageTypeEnum } from '@core/enums';
import { Client } from '@core/interfaces/client.interface';

import { Observable, of } from 'rxjs';
import { finalize, shareReplay, tap } from 'rxjs/operators';
import { ApiUrlService } from '../common/api-url.service';

export interface Slug {
  slug: string;
}

@Injectable({
  providedIn: 'root',
})
export class ApiClientService {
  static CACHED_CLIENT: Client;

  /**
   * Czas życia wpisu w pamięci podręcznej.
   *
   * Konfiguracja klienta niesie tryb pakowania i limity sztuk w paczce, czyli dane, z których
   * koszyk liczy koszt dostawy. Wcześniej wpis żył do pełnego przeładowania dokumentu, więc
   * zmiana ustawień w panelu nie docierała do otwartej zakładki. Minuta odpowiada nagłówkowi
   * Cache-Control wystawianemu przez backend na tym samym adresie.
   */
  static readonly CACHE_TTL_MS = 60 * 1000;

  /** Kiedy wpis trafił do pamięci — do wyliczenia, czy TTL już minął. */
  private static zapisanoO = 0;

  /**
   * Żądanie w locie. Na stronie koszyka getClient() wołają trzy różne miejsca; bez tego
   * pola każde z nich wysyłało własne zapytanie, zanim wróciła pierwsza odpowiedź.
   */
  private static wLocie: Observable<Client> | null = null;

  private apiUrl: string = this.apiUrlService.getApiUrl() + 'client/clients/';
  private apiUrlForceHttps: string = this.apiUrlService.getApiUrl(true) + 'client/clients/';

  constructor(
    private httpClient: HttpClient,
    private apiUrlService: ApiUrlService,
    @Inject(PLATFORM_ID) private platformId: any,
  ) {
  }

  getClient(forceHttps = false): Observable<Client> {
    if (ApiClientService.CACHED_CLIENT && !this.wpisPrzeterminowany()) {
      return of(ApiClientService.CACHED_CLIENT);
    }

    if (ApiClientService.wLocie) {
      return ApiClientService.wLocie;
    }

    const isBrowser = isPlatformBrowser(this.platformId);
    const headers = isBrowser ? {'x-show-loader': '1'} : undefined;
    const url = (forceHttps ? this.apiUrlForceHttps : this.apiUrl) + 'current';

    ApiClientService.wLocie = this.httpClient.get<any>(url, {headers}).pipe(
      tap(client => {
        ApiClientService.CACHED_CLIENT = client;
        ApiClientService.zapisanoO = Date.now();
      }),
      // Zwalniamy uchwyt także po błędzie — inaczej jedna nieudana odpowiedź backendu
      // odcięłaby konfigurację do końca życia zakładki.
      finalize(() => ApiClientService.wLocie = null),
      shareReplay({bufferSize: 1, refCount: false}),
    );

    return ApiClientService.wLocie;
  }

  /** Czy wpis w pamięci jest starszy niż TTL. Pusta pamięć nie jest przeterminowana, tylko pusta. */
  private wpisPrzeterminowany(): boolean {
    return Date.now() - ApiClientService.zapisanoO >= ApiClientService.CACHE_TTL_MS;
  }

  getPageSlug(pageType: clientPageTypeEnum): Observable<Slug> {
    const aliUrl = this.apiUrlService.getApiUrl();
    return this.httpClient.get<Slug>(`${aliUrl}client/page-slug/${pageType}`);
  }


}
